(() => {
  'use strict';

const MICROMETERS_PER_INCH = 25400;
const MICROMETERS_PER_FOOT = MICROMETERS_PER_INCH * 12;
const MICROMETERS_PER_CENTIMETER = 10000;
const SQUARE_MICROMETERS_PER_SQUARE_FOOT = MICROMETERS_PER_FOOT ** 2;
const SQUARE_MICROMETERS_PER_SQUARE_METER = 1000000 ** 2;

class CalculatorError extends Error {
  constructor(code, field = '') {
    super(code);
    this.name = 'CalculatorError';
    this.code = code;
    this.field = field;
  }
}

function parseDecimal(value, field = '') {
  const normalized = String(value ?? '').trim().replace(',', '.');
  if (!/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(normalized)) {
    throw new CalculatorError('invalid_number', field);
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    throw new CalculatorError('invalid_number', field);
  }
  return parsed;
}

function measurementToMicrometers(measurement, field = '') {
  let value;
  if (measurement.mode === 'ft-in') {
    const feet = String(measurement.feet ?? '').trim() === '' ? 0 : parseDecimal(measurement.feet, field);
    const inches = String(measurement.inches ?? '').trim() === '' ? 0 : parseDecimal(measurement.inches, field);
    value = Math.round((feet * MICROMETERS_PER_FOOT) + (inches * MICROMETERS_PER_INCH));
  } else if (measurement.mode === 'in') {
    value = Math.round(parseDecimal(measurement.inches, field) * MICROMETERS_PER_INCH);
  } else if (measurement.mode === 'cm') {
    value = Math.round(parseDecimal(measurement.centimeters, field) * MICROMETERS_PER_CENTIMETER);
  } else {
    throw new CalculatorError('invalid_unit', field);
  }
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new CalculatorError('positive_number_required', field);
  }
  return value;
}

function decimalToMicrometers(value, unit, field = '') {
  if (unit === 'in') {
    return measurementToMicrometers({ mode: 'in', inches: value }, field);
  }
  if (unit === 'cm') {
    return measurementToMicrometers({ mode: 'cm', centimeters: value }, field);
  }
  throw new CalculatorError('invalid_unit', field);
}

function roundDisplay(value, decimals = 3) {
  const factor = 10 ** decimals;
  const rounded = Math.round((value + Number.EPSILON) * factor) / factor;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function formatNumber(value, locale = 'en-US', decimals = 3) {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: 0,
  }).format(roundDisplay(value, decimals));
}

function formatMeasurement(value, mode = 'ft-in', locale = 'en-US') {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new CalculatorError('invalid_number');
  }
  if (mode === 'ft-in') {
    let totalInches = roundDisplay(value / MICROMETERS_PER_INCH, 3);
    let feet = Math.floor(totalInches / 12);
    let inches = roundDisplay(totalInches - (feet * 12), 3);
    if (inches >= 12) {
      feet += 1;
      inches = 0;
    }
    return `${formatNumber(feet, locale, 0)} ft ${formatNumber(inches, locale)} in`;
  }
  if (mode === 'in') {
    return `${formatNumber(value / MICROMETERS_PER_INCH, locale)} in`;
  }
  if (mode === 'cm') {
    return `${formatNumber(value / MICROMETERS_PER_CENTIMETER, locale, 2)} cm`;
  }
  throw new CalculatorError('invalid_unit');
}

function ceilDiv(numerator, denominator) {
  if (!Number.isSafeInteger(numerator) || numerator < 0 || !Number.isSafeInteger(denominator) || denominator <= 0) {
    throw new CalculatorError('invalid_division');
  }
  return Math.floor((numerator + denominator - 1) / denominator);
}

function calculateWorkingRect({ leftHeight, rightHeight, topWidth, bottomWidth, edgeAllowance = MICROMETERS_PER_INCH }) {
  for (const [field, value] of Object.entries({ leftHeight, rightHeight, topWidth, bottomWidth, edgeAllowance })) {
    if (!Number.isSafeInteger(value) || value <= 0) {
      throw new CalculatorError('positive_number_required', field);
    }
  }
  const rawHeight = Math.max(leftHeight, rightHeight);
  const rawWidth = Math.max(topWidth, bottomWidth);
  return {
    rawHeight,
    rawWidth,
    workingHeight: rawHeight + (2 * edgeAllowance),
    workingWidth: rawWidth + (2 * edgeAllowance),
    heightSource: leftHeight === rightHeight ? 'equal' : leftHeight > rightHeight ? 'left' : 'right',
    widthSource: topWidth === bottomWidth ? 'equal' : topWidth > bottomWidth ? 'top' : 'bottom',
    edgeAllowance,
  };
}

function calculateStripCount({ workingWidth, coverageWidth, seamMethod = 'butt', seamOverlap = 0, widthDefinition = 'physical' }) {
  if (!Number.isSafeInteger(workingWidth) || workingWidth <= 0) {
    throw new CalculatorError('positive_number_required', 'workingWidth');
  }
  if (!Number.isSafeInteger(coverageWidth) || coverageWidth <= 0) {
    throw new CalculatorError('positive_number_required', 'coverageWidth');
  }
  if (widthDefinition === 'net') {
    return {
      strips: ceilDiv(workingWidth, coverageWidth),
      firstStripCoverage: coverageWidth,
      followingStripCoverage: coverageWidth,
      overlapApplied: 0,
    };
  }
  if (widthDefinition !== 'physical') {
    throw new CalculatorError('invalid_width_definition', 'widthDefinition');
  }
  if (seamMethod === 'butt') {
    return {
      strips: ceilDiv(workingWidth, coverageWidth),
      firstStripCoverage: coverageWidth,
      followingStripCoverage: coverageWidth,
      overlapApplied: 0,
    };
  }
  if (seamMethod !== 'overlap' && seamMethod !== 'double-cut') {
    throw new CalculatorError('invalid_seam_method', 'seamMethod');
  }
  if (!Number.isSafeInteger(seamOverlap) || seamOverlap <= 0 || seamOverlap >= coverageWidth) {
    throw new CalculatorError('invalid_overlap', 'seamOverlap');
  }
  const followingStripCoverage = coverageWidth - seamOverlap;
  const strips = workingWidth <= coverageWidth
    ? 1
    : 1 + ceilDiv(workingWidth - coverageWidth, followingStripCoverage);
  return {
    strips,
    firstStripCoverage: coverageWidth,
    followingStripCoverage,
    overlapApplied: seamOverlap,
  };
}

function calculateRollEstimate({
  workingHeight,
  workingWidth,
  rollWidth,
  rollLength,
  seamMethod = 'butt',
  seamOverlap = 0,
  widthDefinition = 'physical',
  matchType = 'random',
  patternRepeat = 0,
  fallbackEnabled = false,
  fallbackAllowance = 0,
}) {
  for (const [field, value] of Object.entries({ workingHeight, workingWidth, rollWidth, rollLength })) {
    if (!Number.isSafeInteger(value) || value <= 0) {
      throw new CalculatorError('positive_number_required', field);
    }
  }
  const stripResult = calculateStripCount({
    workingWidth,
    coverageWidth: rollWidth,
    seamMethod,
    seamOverlap,
    widthDefinition,
  });
  if (matchType === 'half-drop') {
    return {
      status: 'manual_verification',
      confidence: 'manual',
      matchType,
      ...stripResult,
    };
  }
  let cutLength = workingHeight;
  let appliedFallback = 0;
  let confidence = 'verified_input';
  if (matchType === 'straight') {
    if (!Number.isSafeInteger(patternRepeat) || patternRepeat <= 0) {
      throw new CalculatorError('pattern_repeat_required', 'patternRepeat');
    }
    cutLength = ceilDiv(workingHeight, patternRepeat) * patternRepeat;
  } else if (matchType === 'unknown') {
    if (!fallbackEnabled || !Number.isSafeInteger(fallbackAllowance) || fallbackAllowance <= 0) {
      throw new CalculatorError('fallback_confirmation_required', 'fallbackAllowance');
    }
    appliedFallback = fallbackAllowance;
    confidence = 'approximate';
  } else if (matchType !== 'random') {
    throw new CalculatorError('invalid_match_type', 'matchType');
  }
  const usableRollLength = rollLength - appliedFallback;
  if (usableRollLength <= 0) {
    throw new CalculatorError('fallback_exceeds_roll', 'fallbackAllowance');
  }
  const dropsPerRoll = Math.floor(usableRollLength / cutLength);
  if (dropsPerRoll < 1) {
    throw new CalculatorError('roll_too_short', 'rollLength');
  }
  const rolls = ceilDiv(stripResult.strips, dropsPerRoll);
  return {
    status: 'complete',
    confidence,
    matchType,
    cutLength,
    usableRollLength,
    dropsPerRoll,
    rolls,
    patternRepeat: matchType === 'straight' ? patternRepeat : 0,
    fallbackAllowance: appliedFallback,
    ...stripResult,
  };
}

function calculateMuralEstimate({
  workingHeight,
  workingWidth,
  panelWidth,
  seamMethod = 'butt',
  seamOverlap = 0,
  widthDefinition = 'physical',
}) {
  if (!Number.isSafeInteger(workingHeight) || workingHeight <= 0) {
    throw new CalculatorError('positive_number_required', 'workingHeight');
  }
  const stripResult = calculateStripCount({
    workingWidth,
    coverageWidth: panelWidth,
    seamMethod,
    seamOverlap,
    widthDefinition,
  });
  return {
    status: 'complete',
    confidence: 'verified_input',
    requiredHeight: workingHeight,
    requiredWidth: workingWidth,
    panels: stripResult.strips,
    ...stripResult,
  };
}

function coverageAreaToSquareMicrometers(value, unit, field = 'adhesiveCoverage') {
  const parsed = parseDecimal(value, field);
  if (parsed <= 0) {
    throw new CalculatorError('positive_number_required', field);
  }
  if (unit === 'sq-ft') {
    return parsed * SQUARE_MICROMETERS_PER_SQUARE_FOOT;
  }
  if (unit === 'sq-m') {
    return parsed * SQUARE_MICROMETERS_PER_SQUARE_METER;
  }
  throw new CalculatorError('invalid_area_unit', field);
}

function calculateAdhesivePacks({ workingHeight, workingWidth, coverageArea }) {
  for (const [field, value] of Object.entries({ workingHeight, workingWidth, coverageArea })) {
    if (!Number.isFinite(value) || value <= 0) {
      throw new CalculatorError('positive_number_required', field);
    }
  }
  const wallArea = workingHeight * workingWidth;
  return {
    wallArea,
    packs: Math.ceil(wallArea / coverageArea),
  };
}


const MAX_LENGTH_MICROMETERS = MICROMETERS_PER_INCH * 2400;

function initializeWallpaperCalculator(root) {
  if (!root || root.dataset.mwcalcInitialized === 'true') return;
  root.dataset.mwcalcInitialized = 'true';

  const form = root.querySelector('[data-mwcalc-form]');
  const configNode = root.querySelector('[data-mwcalc-config]');
  if (!form || !configNode) return;

  let config;
  try {
    config = JSON.parse(configNode.textContent);
  } catch (_error) {
    return;
  }

  const strings = config.strings;
  const locale = config.locale || 'en-US';
  const edgeAllowance = decimalToMicrometers(config.edgeAllowanceInches || 1, 'in', 'edgeAllowance');
  const stepOne = root.querySelector('[data-step="1"]');
  const stepTwo = root.querySelector('[data-step="2"]');
  const resultNode = root.querySelector('[data-result]');
  const wallMath = root.querySelector('[data-wall-math]');
  const initialUnit = checkedValue('units') || 'in';
  let previousUnit = initialUnit;
  let resultText = '';
  let started = false;

  function all(selector) {
    return Array.from(root.querySelectorAll(selector));
  }

  function control(name) {
    return root.querySelector(`[data-control="${name}"]`);
  }

  function checkedValue(name) {
    const selected = root.querySelector(`[data-control="${name}"]:checked`);
    return selected ? selected.value : '';
  }

  function selectedValue(name) {
    const node = control(name);
    return node ? node.value : '';
  }

  function template(text, values) {
    return Object.entries(values).reduce(
      (output, [key, value]) => output.split(`{${key}}`).join(String(value)),
      text,
    );
  }

  function plural(count, singular, pluralText) {
    return count === 1 ? singular : pluralText;
  }

  function track(eventName, extra = {}) {
    const mode = checkedValue('mode') || 'roll';
    const material = selectedValue('material') || 'peel';
    const safePayload = {
      event: eventName,
      calculator_mode: mode,
      calculator_unit: checkedValue('units') || previousUnit,
      calculator_material_type: material,
      calculator_seam_method: selectedValue('seamMethod') || 'butt',
      calculator_match_type: mode === 'roll' ? selectedValue('matchType') || 'random' : 'not_applicable',
      ...extra,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(safePayload);
  }

  function setHidden(node, hidden) {
    if (!node) return;
    node.hidden = hidden;
    node.querySelectorAll('input, select, button, textarea').forEach((input) => {
      input.disabled = hidden;
    });
  }

  function measurementGroup(key) {
    return root.querySelector(`[data-measure="${key}"]`);
  }

  function rawMeasurement(group, unit) {
    if (!group) throw new CalculatorError('invalid_number');
    const value = (part) => {
      const input = group.querySelector(`[data-unit-view="${unit}"] [data-part="${part}"]`);
      return input ? input.value : '';
    };
    if (unit === 'ft-in') {
      return { mode: unit, feet: value('feet'), inches: value('inches') };
    }
    if (unit === 'in') {
      return { mode: unit, inches: value('inches') };
    }
    return { mode: unit, centimeters: value('centimeters') };
  }

  function measurementIsBlank(measurement) {
    if (measurement.mode === 'ft-in') {
      return String(measurement.feet || '').trim() === '' && String(measurement.inches || '').trim() === '';
    }
    if (measurement.mode === 'in') return String(measurement.inches || '').trim() === '';
    return String(measurement.centimeters || '').trim() === '';
  }

  function readMeasurement(key, unit = checkedValue('units') || previousUnit) {
    const measurement = rawMeasurement(measurementGroup(key), unit);
    const value = measurementToMicrometers(measurement, key);
    if (value > MAX_LENGTH_MICROMETERS) {
      throw new CalculatorError('measurement_too_large', key);
    }
    return value;
  }

  function roundInput(value, decimals = 3) {
    return String(roundDisplay(value, decimals));
  }

  function populateMeasurement(group, unit, micrometers) {
    const view = group.querySelector(`[data-unit-view="${unit}"]`);
    if (!view) return;
    if (micrometers === null) {
      view.querySelectorAll('[data-measure-input]').forEach((input) => {
        input.value = '';
      });
      return;
    }
    if (unit === 'ft-in') {
      const totalInches = micrometers / MICROMETERS_PER_INCH;
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches - (feet * 12);
      view.querySelector('[data-part="feet"]').value = feet ? String(feet) : '';
      view.querySelector('[data-part="inches"]').value = roundInput(inches);
    } else if (unit === 'in') {
      view.querySelector('[data-part="inches"]').value = roundInput(micrometers / MICROMETERS_PER_INCH);
    } else {
      view.querySelector('[data-part="centimeters"]').value = roundInput(micrometers / MICROMETERS_PER_CENTIMETER, 2);
    }
  }

  function switchUnits(nextUnit) {
    all('[data-measure]').forEach((group) => {
      const source = rawMeasurement(group, previousUnit);
      let micrometers = null;
      if (!measurementIsBlank(source)) {
        try {
          micrometers = measurementToMicrometers(source, group.dataset.measure);
        } catch (_error) {
          micrometers = null;
        }
      }
      populateMeasurement(group, nextUnit, micrometers);
      group.querySelectorAll('[data-unit-view]').forEach((view) => {
        const hidden = view.dataset.unitView !== nextUnit;
        view.hidden = hidden;
        view.querySelectorAll('[data-measure-input]').forEach((input) => {
          input.disabled = hidden || Boolean(group.closest('[hidden]'));
        });
      });
    });
    previousUnit = nextUnit;
    hideResult();
    tryUpdateWallMath();
  }

  function mode() {
    return checkedValue('mode') || 'roll';
  }

  function updateConditions() {
    const currentMode = mode();
    all('[data-mode-panel]').forEach((panel) => setHidden(panel, panel.dataset.modePanel !== currentMode));
    all('[data-roll-only]').forEach((node) => setHidden(node, currentMode !== 'roll'));

    const matchType = selectedValue('matchType') || 'random';
    all('[data-condition="straight"]').forEach((node) => setHidden(node, currentMode !== 'roll' || matchType !== 'straight'));
    all('[data-condition="unknown"]').forEach((node) => setHidden(node, currentMode !== 'roll' || matchType !== 'unknown'));

    const fallbackEnabled = Boolean(control('fallbackEnabled') && control('fallbackEnabled').checked);
    all('[data-fallback-input]').forEach((node) => setHidden(node, currentMode !== 'roll' || matchType !== 'unknown' || !fallbackEnabled));

    const seamMethod = selectedValue('seamMethod') || 'butt';
    const widthDefinition = selectedValue('widthDefinition') || 'physical';
    const needsSeamInput = seamMethod !== 'butt' && widthDefinition === 'physical';
    all('[data-condition="seam"]').forEach((node) => setHidden(node, !needsSeamInput));
    all('[data-double-cut-warning]').forEach((node) => {
      node.hidden = seamMethod !== 'double-cut';
    });

    const material = selectedValue('material') || 'peel';
    all('[data-condition="adhesive"]').forEach((node) => setHidden(node, material !== 'unpasted'));
    const note = root.querySelector('[data-material-note]');
    if (note) {
      note.textContent = material === 'prepasted' ? strings.prepastedNote : material === 'unpasted' ? strings.unpastedNote : strings.peelNote;
    }
  }

  function clearErrors() {
    all('[data-field-error], [data-adhesive-error]').forEach((node) => {
      node.hidden = true;
      node.textContent = '';
    });
    all('[aria-invalid="true"]').forEach((node) => node.removeAttribute('aria-invalid'));
    all('[data-error-summary]').forEach((node) => {
      node.hidden = true;
    });
  }

  function errorMessage(code) {
    return strings.errors[code] || strings.errors.generic;
  }

  function renderError(error, shouldFocus = true) {
    const normalized = error instanceof CalculatorError ? error : new CalculatorError('generic');
    const message = errorMessage(normalized.code);
    let target = normalized.field ? measurementGroup(normalized.field) : null;
    let errorNode = target ? target.querySelector('[data-field-error]') : null;

    if (!target && normalized.field === 'adhesiveCoverage') {
      target = control('adhesiveCoverage');
      errorNode = root.querySelector('[data-adhesive-error]');
    }
    if (!target && normalized.field) {
      target = control(normalized.field);
    }

    const activeStep = stepTwo.hidden ? stepOne : stepTwo;
    const summary = activeStep.querySelector('[data-error-summary]');
    if (summary) {
      summary.textContent = `${strings.errorSummary} ${message}`;
      summary.hidden = false;
    }
    if (errorNode) {
      errorNode.textContent = message;
      errorNode.hidden = false;
    }
    const focusTarget = target && target.matches('input, select')
      ? target
      : target && target.querySelector('input:not([disabled]), select:not([disabled])');
    if (focusTarget) focusTarget.setAttribute('aria-invalid', 'true');
    if (shouldFocus) {
      if (focusTarget) focusTarget.focus();
      else if (summary) summary.focus();
    }
    track('wallpaper_calculator_error', { calculator_error_code: normalized.code });
  }

  function wallRect() {
    return calculateWorkingRect({
      leftHeight: readMeasurement('leftHeight'),
      rightHeight: readMeasurement('rightHeight'),
      topWidth: readMeasurement('topWidth'),
      bottomWidth: readMeasurement('bottomWidth'),
      edgeAllowance,
    });
  }

  function sourceText(axis, source) {
    const key = axis === 'height'
      ? source === 'left' ? 'sourceLeft' : source === 'right' ? 'sourceRight' : 'sourceHeightEqual'
      : source === 'top' ? 'sourceTop' : source === 'bottom' ? 'sourceBottom' : 'sourceWidthEqual';
    return strings[key] || '';
  }

  function updateWallMath(rect) {
    const unit = checkedValue('units') || previousUnit;
    const values = {
      allowance: formatMeasurement(rect.edgeAllowance, unit, locale),
    };
    const heightLine = template(strings.mathHeight, {
      ...values,
      raw: formatMeasurement(rect.rawHeight, unit, locale),
      working: formatMeasurement(rect.workingHeight, unit, locale),
    });
    const widthLine = template(strings.mathWidth, {
      ...values,
      raw: formatMeasurement(rect.rawWidth, unit, locale),
      working: formatMeasurement(rect.workingWidth, unit, locale),
    });
    root.querySelector('[data-wall-height-math]').textContent = `${heightLine} ${sourceText('height', rect.heightSource)}`.trim();
    root.querySelector('[data-wall-width-math]').textContent = `${widthLine} ${sourceText('width', rect.widthSource)}`.trim();
    wallMath.hidden = false;
  }

  function tryUpdateWallMath() {
    try {
      updateWallMath(wallRect());
    } catch (_error) {
      wallMath.hidden = true;
    }
  }

  function hideResult() {
    resultNode.hidden = true;
    resultText = '';
  }

  function showStep(number) {
    stepOne.hidden = number !== 1;
    stepTwo.hidden = number !== 2;
    hideResult();
    const heading = (number === 1 ? stepOne : stepTwo).querySelector('.mwcalc-step-heading');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    }
  }

  function seamInputs(unit) {
    const seamMethod = selectedValue('seamMethod') || 'butt';
    const widthDefinition = selectedValue('widthDefinition') || 'physical';
    return {
      seamMethod,
      widthDefinition,
      seamOverlap: seamMethod === 'butt' || widthDefinition === 'net' ? 0 : readMeasurement('seamOverlap', unit),
    };
  }

  function setResultRow(name, value, hidden = false) {
    const row = root.querySelector(`[data-result-row="${name}"]`);
    if (row) row.hidden = hidden;
    const valueNode = root.querySelector(`[data-result-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}]`);
    if (valueNode && value !== undefined) valueNode.textContent = value;
  }

  function quantityBucket(count) {
    if (count <= 1) return '1';
    if (count === 2) return '2';
    if (count === 3) return '3';
    if (count <= 5) return '4-5';
    return '6+';
  }

  function renderResult(rect, estimate, context) {
    const unit = context.unit;
    const f = (value) => formatMeasurement(value, unit, locale);
    const lines = [];
    lines.push(`${template(strings.mathHeight, { raw: f(rect.rawHeight), allowance: f(rect.edgeAllowance), working: f(rect.workingHeight) })} ${sourceText('height', rect.heightSource)}`.trim());
    lines.push(`${template(strings.mathWidth, { raw: f(rect.rawWidth), allowance: f(rect.edgeAllowance), working: f(rect.workingWidth) })} ${sourceText('width', rect.widthSource)}`.trim());

    root.querySelector('[data-result-working-size]').textContent = `${f(rect.workingWidth)} × ${f(rect.workingHeight)}`;
    root.querySelector('[data-result-strips]').textContent = String(estimate.strips);
    root.querySelector('[data-result-seam]').textContent = context.seamLabel;

    if (estimate.overlapApplied > 0) {
      lines.push(template(strings.formulaOverlap, {
        first: f(estimate.firstStripCoverage),
        following: f(estimate.followingStripCoverage),
        overlap: f(estimate.overlapApplied),
        strips: estimate.strips,
      }));
    } else {
      lines.push(template(context.widthDefinition === 'net' ? strings.formulaNet : strings.formulaButt, {
        width: f(rect.workingWidth),
        coverage: f(estimate.followingStripCoverage),
        strips: estimate.strips,
      }));
    }

    const manual = estimate.status === 'manual_verification';
    const manualNote = root.querySelector('[data-manual-note]');
    manualNote.hidden = !manual;

    if (context.mode === 'mural') {
      root.querySelector('[data-result-quantity]').textContent = template(strings.muralRequired, {
        width: f(estimate.requiredWidth),
        height: f(estimate.requiredHeight),
      });
      setResultRow('cutLength', '', true);
      setResultRow('drops', '', true);
      setResultRow('repeat', strings.notApplicable, false);
      root.querySelector('[data-result-confidence]').textContent = strings.resultVerified;
      lines.push(template(strings.formulaMural, {
        width: f(estimate.requiredWidth),
        height: f(estimate.requiredHeight),
        panels: estimate.panels,
      }));
    } else if (manual) {
      root.querySelector('[data-result-quantity]').textContent = strings.resultManual;
      setResultRow('cutLength', strings.notApplicable, true);
      setResultRow('drops', strings.notApplicable, true);
      setResultRow('repeat', context.matchLabel, false);
      root.querySelector('[data-result-confidence]').textContent = strings.resultManual;
    } else {
      root.querySelector('[data-result-quantity]').textContent = `${estimate.rolls} ${plural(estimate.rolls, strings.rollSingular, strings.rollPlural)}`;
      setResultRow('cutLength', f(estimate.cutLength), false);
      setResultRow('drops', String(estimate.dropsPerRoll), false);
      const repeatValue = estimate.matchType === 'straight'
        ? f(estimate.patternRepeat)
        : estimate.matchType === 'unknown'
          ? `${f(estimate.fallbackAllowance)} ${strings.approximateLabel}`
          : context.matchLabel;
      setResultRow('repeat', repeatValue, false);
      root.querySelector('[data-result-confidence]').textContent = estimate.confidence === 'approximate' ? strings.resultApproximate : strings.resultVerified;
      if (estimate.matchType === 'straight') {
        lines.push(template(strings.formulaStraight, {
          height: f(rect.workingHeight),
          cutLength: f(estimate.cutLength),
          repeat: f(estimate.patternRepeat),
          rollLength: f(context.rollLength),
          drops: estimate.dropsPerRoll,
        }));
      } else {
        lines.push(template(strings.formulaRandom, {
          height: f(estimate.cutLength),
          rollLength: f(context.rollLength),
          drops: estimate.dropsPerRoll,
        }));
      }
      if (estimate.matchType === 'unknown') {
        lines.push(template(strings.formulaUnknown, { fallback: f(estimate.fallbackAllowance) }));
      }
    }

    const adhesiveRow = root.querySelector('[data-result-row="adhesive"]');
    if (context.adhesivePacks) {
      adhesiveRow.hidden = false;
      root.querySelector('[data-result-adhesive]').textContent = `${context.adhesivePacks} ${plural(context.adhesivePacks, strings.packSingular, strings.packPlural)}`;
    } else {
      adhesiveRow.hidden = true;
    }

    const list = root.querySelector('[data-calculation-lines]');
    list.replaceChildren(...lines.map((line) => {
      const item = document.createElement('li');
      item.textContent = line;
      return item;
    }));

    const resultLines = [
      strings.resultTitle,
      root.querySelector('[data-result-quantity]').textContent,
      `${strings.resultWorkingSize}: ${root.querySelector('[data-result-working-size]').textContent}`,
      `${strings.resultStrips}: ${estimate.strips}`,
      ...lines,
      strings.disclaimer,
    ];
    resultText = resultLines.join('\n');

    resultNode.hidden = false;
    resultNode.focus();
    const analytics = { calculator_result_confidence: estimate.confidence };
    if (context.mode === 'roll' && !manual) analytics.calculator_roll_count_bucket = quantityBucket(estimate.rolls);
    track('wallpaper_calculator_complete', analytics);
  }

  function calculate(event) {
    event.preventDefault();
    clearErrors();
    hideResult();
    try {
      const unit = checkedValue('units') || previousUnit;
      const rect = wallRect();
      const currentMode = mode();
      const seam = seamInputs(unit);
      const seamSelect = control('seamMethod');
      const context = {
        unit,
        mode: currentMode,
        widthDefinition: seam.widthDefinition,
        seamLabel: seamSelect.options[seamSelect.selectedIndex].text,
        matchLabel: '',
        rollLength: 0,
        adhesivePacks: 0,
      };
      let estimate;

      if (currentMode === 'roll') {
        const matchType = selectedValue('matchType') || 'random';
        const matchSelect = control('matchType');
        context.matchLabel = matchSelect.options[matchSelect.selectedIndex].text;
        context.rollLength = readMeasurement('rollLength', unit);
        estimate = calculateRollEstimate({
          workingHeight: rect.workingHeight,
          workingWidth: rect.workingWidth,
          rollWidth: readMeasurement('rollWidth', unit),
          rollLength: context.rollLength,
          ...seam,
          matchType,
          patternRepeat: matchType === 'straight' ? readMeasurement('patternRepeat', unit) : 0,
          fallbackEnabled: matchType === 'unknown' && Boolean(control('fallbackEnabled').checked),
          fallbackAllowance: matchType === 'unknown' && control('fallbackEnabled').checked ? readMeasurement('fallbackAllowance', unit) : 0,
        });
      } else {
        estimate = calculateMuralEstimate({
          workingHeight: rect.workingHeight,
          workingWidth: rect.workingWidth,
          panelWidth: readMeasurement('panelWidth', unit),
          ...seam,
        });
      }

      if (selectedValue('material') === 'unpasted') {
        const adhesiveInput = control('adhesiveCoverage');
        if (adhesiveInput.value.trim() !== '') {
          const coverageArea = coverageAreaToSquareMicrometers(
            adhesiveInput.value,
            selectedValue('adhesiveUnit'),
            'adhesiveCoverage',
          );
          context.adhesivePacks = calculateAdhesivePacks({
            workingHeight: rect.workingHeight,
            workingWidth: rect.workingWidth,
            coverageArea,
          }).packs;
        }
      }

      renderResult(rect, estimate, context);
    } catch (error) {
      renderError(error);
    }
  }

  root.querySelector('[data-next]').addEventListener('click', () => {
    clearErrors();
    try {
      updateWallMath(wallRect());
      if (!started) {
        track('wallpaper_calculator_start');
        started = true;
      }
      showStep(2);
      updateConditions();
    } catch (error) {
      renderError(error);
    }
  });

  root.querySelector('[data-back]').addEventListener('click', () => {
    clearErrors();
    showStep(1);
    tryUpdateWallMath();
  });

  form.addEventListener('submit', calculate);
  form.addEventListener('input', (event) => {
    if (event.target.matches('[data-control="fallbackEnabled"]')) updateConditions();
    hideResult();
  });
  form.addEventListener('change', (event) => {
    if (event.target.matches('[data-control="units"]')) {
      switchUnits(event.target.value);
    }
    if (event.target.matches('[data-control="mode"], [data-control="matchType"], [data-control="seamMethod"], [data-control="widthDefinition"], [data-control="material"]')) {
      updateConditions();
      hideResult();
    }
  });
  form.addEventListener('focusout', (event) => {
    if (event.target.matches('[data-measure="leftHeight"] input, [data-measure="rightHeight"] input, [data-measure="topWidth"] input, [data-measure="bottomWidth"] input')) {
      tryUpdateWallMath();
    }
  });

  root.querySelector('[data-copy]').addEventListener('click', async (event) => {
    if (!resultText) return;
    const button = event.currentTarget;
    try {
      await navigator.clipboard.writeText(resultText);
    } catch (_error) {
      const helper = document.createElement('textarea');
      helper.value = resultText;
      helper.setAttribute('readonly', '');
      helper.style.position = 'fixed';
      helper.style.opacity = '0';
      document.body.appendChild(helper);
      helper.select();
      document.execCommand('copy');
      helper.remove();
    }
    button.textContent = strings.copied;
    window.setTimeout(() => {
      button.textContent = strings.copy;
    }, 1600);
    track('wallpaper_calculator_copy');
  });

  const emailButton = root.querySelector('[data-email]');
  if (emailButton) {
    emailButton.addEventListener('click', () => {
      if (!resultText) return;
      track('wallpaper_calculator_email_request');
      const mailto = `mailto:?subject=${encodeURIComponent(strings.emailSubject)}&body=${encodeURIComponent(resultText)}`;
      window.location.assign(mailto);
    });
  }

  root.querySelector('[data-print]').addEventListener('click', () => {
    track('wallpaper_calculator_print');
    window.print();
  });

  root.querySelector('[data-reset]').addEventListener('click', () => {
    form.reset();
    previousUnit = initialUnit;
    switchUnits(initialUnit);
    clearErrors();
    wallMath.hidden = true;
    updateConditions();
    showStep(1);
    started = false;
  });

  all('[data-product-link]').forEach((link) => {
    link.addEventListener('click', () => {
      track('wallpaper_calculator_product_click', { calculator_destination: link.dataset.productLink });
    });
  });

  switchUnits(initialUnit);
  updateConditions();
  track('wallpaper_calculator_view');
}

function initializeAllWallpaperCalculators(scope = document) {
  scope.querySelectorAll('[data-mwcalc-root]').forEach(initializeWallpaperCalculator);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeAllWallpaperCalculators());
} else {
  initializeAllWallpaperCalculators();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeAllWallpaperCalculators(event.target);
});

})();
